/**
 * Forge Score Curve — computes Forge Score across a sweep of arrow weights
 * for a given bow + environment + species, so the UI can render a chart
 * with a "sweet spot" highlighted band.
 *
 * The shape:
 *   Light arrow → high speed, low momentum, modest pen → low-mid score
 *   Mid arrow   → balance → highest score
 *   Heavy arrow → low speed, high momentum, slower TOF, more drift → score
 *                 drops on light game (drift dominates) or peaks on heavy
 *                 game (momentum dominates)
 *
 * The species profile tilts the curve.
 */

import type { Build, Environment, Distance } from '../trajectory/types';
import { computeForgeScore } from './index';
import type { SpeciesProfile } from './speciesProfiles';

export interface CurvePoint {
  arrowGrains: number;
  forgeScore: number;
  tier: string;
  retainedKePct: number;
  driftIn: number;
  apexIn: number;
}

export interface CurveResult {
  points: CurvePoint[];
  sweetSpot: { startGrains: number; endGrains: number } | null;
  bestPoint: CurvePoint;
  currentPoint: CurvePoint;
  /**
   * Hard minimum arrow weight enforced by the species profile (typically
   * for dangerous game). Points below this weight render greyed-out and
   * are excluded from `bestPoint` + `sweetSpot` selection. null when no
   * species floor applies.
   */
  weightFloorGrains: number | null;
  /** Plain-English reason for the floor — surfaced in the UI when present. */
  weightFloorReason: string | null;
  /** Effective per-axis weights actually applied for this species (sums to 100). */
  effectiveWeights: {
    tuneQuality: number;
    momentum: number;
    effectiveFocPenetration: number;
    energyRetention: number;
  };
  /** True when bone-defeating multiplier is active for this species
   *  (dangerous game). Lets the UI explain why mech heads were penalized. */
  bonePenetrationActive: boolean;
}

const WEIGHT_MIN = 320;
const WEIGHT_MAX = 700;
const WEIGHT_STEP = 10;

/**
 * Compute the Forge Score curve over a sweep of arrow weights, holding
 * everything else constant. The current build is preserved unchanged at
 * its own weight (returned as `currentPoint`).
 *
 * The species profile adjusts the underlying score computation by biasing
 * which scenario / range the score is computed against — heavier game pulls
 * the score's weight toward momentum, target game pulls toward retention.
 */
export function computeForgeScoreCurve(
  build: Build,
  env: Environment,
  species: SpeciesProfile
): CurveResult {
  const speciesEnv: Environment = {
    ...env,
    shotScenario: species.expectedShotScenario,
    speciesTag: species.tag,
  };
  const dist: Distance = { rangeYards: species.typicalRangeYards };

  const points: CurvePoint[] = [];

  for (let weight = WEIGHT_MIN; weight <= WEIGHT_MAX; weight += WEIGHT_STEP) {
    const swept: Build = { ...build, arrowGrains: weight };
    const score = computeForgeScore({ build: swept, env: speciesEnv, dist });
    points.push({
      arrowGrains: weight,
      forgeScore: score.score,
      tier: score.tier,
      retainedKePct: score.trajectory.retainedKePct,
      driftIn: score.trajectory.driftIn,
      apexIn: score.trajectory.apexHeightIn,
    });
  }

  /* Apply species weight floor: bestPoint and sweetSpot only consider
   * points at or above the floor. The chart still renders ALL points so
   * the user sees why the floor exists — they just can't be "best." */
  const weightFloor = species.minArrowGrains ?? 0;
  const eligible = (p: CurvePoint) => p.arrowGrains >= weightFloor;

  /* Find the longest contiguous run of points at or above sweetSpotMinScore.
   * Points below the species' weight floor are treated as ineligible — they
   * break the run so the sweet spot can't include them. */
  let sweetSpot: { startGrains: number; endGrains: number } | null = null;
  let runStart: number | null = null;
  let bestRun: { start: number; end: number; length: number } | null = null;
  for (let i = 0; i < points.length; i++) {
    if (eligible(points[i]) && points[i].forgeScore >= species.sweetSpotMinScore) {
      if (runStart === null) runStart = i;
      const runLen = i - runStart + 1;
      if (!bestRun || runLen > bestRun.length) {
        bestRun = { start: runStart, end: i, length: runLen };
      }
    } else {
      runStart = null;
    }
  }
  if (bestRun) {
    sweetSpot = {
      startGrains: points[bestRun.start].arrowGrains,
      endGrains: points[bestRun.end].arrowGrains,
    };
  }

  /* Best single point — restricted to eligible (above species floor). If
   * NO eligible points exist (e.g. the swept range is entirely below the
   * floor), fall back to the unconstrained best and let the UI render the
   * floor-violation warning. */
  const eligiblePoints = points.filter(eligible);
  const bestPoint = eligiblePoints.length > 0
    ? eligiblePoints.reduce((best, p) => (p.forgeScore > best.forgeScore ? p : best), eligiblePoints[0])
    : points.reduce((best, p) => (p.forgeScore > best.forgeScore ? p : best), points[0]);

  /* Current point — re-evaluate at the user's selected arrow weight */
  const currentScore = computeForgeScore({ build, env: speciesEnv, dist });
  const currentPoint: CurvePoint = {
    arrowGrains: build.arrowGrains,
    forgeScore: currentScore.score,
    tier: currentScore.tier,
    retainedKePct: currentScore.trajectory.retainedKePct,
    driftIn: currentScore.trajectory.driftIn,
    apexIn: currentScore.trajectory.apexHeightIn,
  };

  return {
    points,
    sweetSpot,
    bestPoint,
    currentPoint,
    weightFloorGrains: species.minArrowGrains ?? null,
    weightFloorReason: species.minArrowGrainsReason ?? null,
    effectiveWeights: currentScore.effectiveWeights,
    bonePenetrationActive: species.gameMassClass === 'dangerous',
  };
}
